import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  FaShareAlt,
  FaRegCalendarAlt,
  FaCoins,
  FaGlobeEurope,
  FaArrowLeft,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { testUrl } from "../BaseData";
import classes from "./GrantlarVaLoyihalar.module.css";
import GrantCard, { deadlineState } from "./GrantCard";
import {
  getLabels,
  normLang,
  audiencePath,
  pick,
  formatDate,
  daysLeft,
  flagUrl,
  imgUrl,
  fixHtml,
  shareLink,
} from "./grantUtils";

const GrantDetail = () => {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const L = getLabels(lang);

  const [grant, setGrant] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${testUrl}/api/grants/${id}/`)
      .then((res) => {
        setGrant(res.data.grant || null);
        setRelated(res.data.related || []);
      })
      .catch((err) =>
        setError(err?.response?.status === 404 ? L.notFound : L.error)
      )
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  if (loading) return <div className={classes.status}>{L.loading}</div>;
  if (error) return <div className={classes.status}>{error}</div>;
  if (!grant) return <div className={classes.status}>{L.notFound}</div>;

  const title = pick(grant, "title", lang);
  const country = pick(grant, "country", lang);
  const funding = pick(grant, "funding", lang);
  const content = pick(grant, "content", lang);
  const flag = flagUrl(grant.country_code);
  const left = daysLeft(grant.deadline);
  const expired = left !== null && left < 0;

  const state = deadlineState(left, L);

  const handleShare = async () => {
    const url = `${window.location.origin}/${normLang(lang)}/grantlar/${grant.id}`;
    const result = await shareLink(url, title);
    if (result === "copied") setToast(L.copied);
  };

  const audienceLabel =
    grant.audience === "all" ? L.everyone : L[grant.audience] || "";
  const scopeLabel = L[grant.scope] || "";

  return (
    <div className={classes.detail}>
      <Link to={audiencePath(grant.audience)} className={classes.back}>
        <FaArrowLeft /> {L.back}
      </Link>

      <div className={classes.detailCard}>
        <div className={classes.detailImageWrap}>
          {/* Orqa fon — rasmning xiralashtirilgan nusxasi; oldinda rasm to'liq (kesilmasdan) */}
          <div
            className={classes.detailImageBg}
            style={{ backgroundImage: `url(${imgUrl(grant.image)})` }}
          />
          <img src={imgUrl(grant.image)} alt={title} className={classes.detailImage} />

          {(country || flag) && (
            <span className={classes.country}>
              {flag && (
                <img
                  src={flag}
                  alt=""
                  className={classes.flag}
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              )}
              {country}
            </span>
          )}

          <button
            type="button"
            className={classes.share}
            onClick={handleShare}
            title={L.share}
            aria-label={L.share}
          >
            <FaShareAlt />
          </button>

          {expired && <span className={classes.expiredBadge}>{L.expired}</span>}
        </div>

        <div className={classes.detailBody}>
          <div className={classes.tags}>
            {audienceLabel && (
              <span className={`${classes.tag} ${classes.tagAudience}`}>{audienceLabel}</span>
            )}
            {scopeLabel && (
              <span className={`${classes.tag} ${classes.tagScope}`}>{scopeLabel}</span>
            )}
          </div>

          <h1 className={classes.detailTitle}>{title}</h1>

          <div className={classes.detailMeta}>
            <div className={classes.stat}>
              <span className={`${classes.statIcon} ${classes.statIconFunding}`}>
                <FaCoins />
              </span>
              <div className={classes.statText}>
                <span className={classes.statLabel}>{L.funding}</span>
                <span className={classes.statValue}>{funding || "—"}</span>
              </div>
            </div>

            <div className={classes.stat}>
              <span className={`${classes.statIcon} ${classes.statIconDeadline}`}>
                <FaRegCalendarAlt />
              </span>
              <div className={classes.statText}>
                <span className={classes.statLabel}>{L.deadline}</span>
                <span className={classes.statRow}>
                  <span
                    className={`${classes.statValue} ${
                      grant.deadline && !expired ? classes.statValueDeadline : ""
                    }`}
                  >
                    {grant.deadline ? formatDate(grant.deadline, lang) : L.noDeadline}
                  </span>
                  {state.text && (
                    <span className={`${classes.pill} ${classes[`pill_${state.tone}`]}`}>
                      {state.text}
                    </span>
                  )}
                </span>
              </div>
            </div>

            {country && (
              <div className={classes.stat}>
                <span className={`${classes.statIcon} ${classes.statIconCountry}`}>
                  <FaGlobeEurope />
                </span>
                <div className={classes.statText}>
                  <span className={classes.statLabel}>{L.country}</span>
                  <span className={`${classes.statValue} ${classes.statRow}`}>
                    {flag && (
                      <img
                        src={flag}
                        alt=""
                        className={classes.flag}
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                    {country}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div
            className={classes.content}
            dangerouslySetInnerHTML={{ __html: fixHtml(content) }}
          />

          {grant.link && !expired && (
            <a
              href={grant.link}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.applyBtn}
            >
              {L.apply} <FaExternalLinkAlt />
            </a>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <>
          <h2 className={classes.relatedTitle}>{L.related}</h2>
          <div className={classes.grid}>
            {related.map((item) => (
              <GrantCard key={item.id} item={item} lang={lang} onToast={setToast} />
            ))}
          </div>
        </>
      )}

      {toast && <div className={classes.toast}>{toast}</div>}
    </div>
  );
};

export default GrantDetail;
